import React, { useEffect, useState } from "react";
import ErrorBoundary from "../../ErrorBoundary";
import SummaryWidget from "../Widgets/Summary/SummaryWidget";
import LayoutControls from "../Widgets/LayoutControls/LayoutControls";
import PropTypes from "prop-types";
import { Translate, getTranslation } from "orion-components/i18n";
import { facilityService } from "client-app-core";
import $ from "jquery";
import PinToDialog from "../../SharedComponents/PinToDialog";
import DeviceEditDialog from "./components/DeviceEditDialog";
import FileWidget from "../Widgets/File/FileWidget";
import Activities from "../Widgets/Activities/Activities";
import LinkedItemsWidget from "../Widgets/LinkedItems/LinkedItemsWidget";
import CamerasWidget from "../Widgets/Cameras/CamerasWidget";
import CameraClusters from "../Widgets/CameraClusters/CameraClusters";
import AccessControlWidget from "../Widgets/AccessControl/AccessControlWidget";
import AlertingZonesWidget from "../Widgets/AlertingZones/AlertingZonesWidget";
import { DetailsWidget, MapWidget } from "../Widgets";
import { useSelector, useDispatch } from "react-redux";
import { selectedContextSelector } from "orion-components/ContextPanel/Selectors";
import { getDir } from "orion-components/i18n/Config/selector";
//actions
import { openDialog, closeDialog } from "orion-components/AppState/Actions";
import {
	startActivityStream,
	startAttachmentStream,
	unsubscribeFromFeed,
	startCamerasInRangeStream,
	unsubscribeFromContext,
	unsubscribe
} from "orion-components/ContextualData/Actions";
import {
	addRemoveFromCollections,
	addRemoveFromEvents,
	unlinkEntities,
	createCollection,
	updateDevice
} from "orion-components/SharedActions/deviceProfileActions";
import { checkPermissionBasedOnFeedId, canManageByApplication } from "orion-components/Profiles/Selectors";
import { deviceWidgetConfig } from "orion-components/Profiles/Selectors/DeviceProfileSelectors";
import isEqual from "lodash/isEqual";
import { feedInfoSelector } from "../../GlobalData/Selectors";
import { streamIndividualEntityByType } from "orion-components/ContextPanel/ContextPanelData/actions";

const propTypes = {
	facilityOption: PropTypes.bool,
	appData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	view: PropTypes.string,
	selectWidget: PropTypes.func,
	endDate: PropTypes.date,
	disableLinkedItems: PropTypes.bool,
	disabledLinkedItemTypes: PropTypes.bool,
	floorPlansWithFacFeed: PropTypes.bool,
	selectFloorPlanOn: PropTypes.func,
	forReplay: PropTypes.bool,
	readOnly: PropTypes.bool,
	widgetsExpandable: PropTypes.bool,
	disableEdit: PropTypes.bool
};

const defaultProps = {
	selectFloorPlanOn: () => { },
	//The `readonly` and `forReplay` props are exclusively provided to this component by the `replay-app`. To ensure the proper functionality of other apps, it is necessary to set both `readonly` and `forReplay` to `false`.
	readOnly: false,
	forReplay: false,
	disableEdit: false
};

let cameraIds = {};

const DeviceProfile = ({
	facilityOption,
	appData,
	view,
	selectWidget,
	endDate,
	disableLinkedItems,
	disabledLinkedItemTypes,
	floorPlansWithFacFeed,
	selectFloorPlanOn,
	widgetsExpandable,
	forReplay,
	readOnly,
	disableEdit
}) => {
	const dispatch = useDispatch();

	const context = useSelector((state) => selectedContextSelector(state));

	const { entity, camerasInRange } = context ?? {};

	const entityType = entity?.entityType;
	const contextId = entity?.id;
	const dir = useSelector((state) => getDir(state) || null);

	const device = entity;
	const { entityData, feedId } = device ?? {};
	const { properties, geometry } = entityData ?? {};
	const { name, description, type } = properties ?? {};

	const floorPlansWithFacilityFeed = useSelector((state) =>
		floorPlansWithFacFeed ? state.globalData.floorPlanWithFacilityFeedId.floorPlans : null
	);

	const [scrolledUp, setScrolledUp] = useState(false);
	const [hiding, setHiding] = useState(false);
	const [layoutControlsOpen, setLayoutControlsOpen] = useState(false);
	const [facilityHidden, setFacilityHidden] = useState(true);

	const canManageDevice = useSelector((state) =>
		checkPermissionBasedOnFeedId(state)(device?.feedId, "permissions", "manage")
	);

	const canControlDevice = useSelector((state) =>
		checkPermissionBasedOnFeedId(state)(device?.feedId, "permissions", "control")
	);

	const canAccess = useSelector(
		(state) => !readOnly && checkPermissionBasedOnFeedId(state)(device?.feedId, "config")
	);
	const canManageEvents = useSelector((state) => canManageByApplication(state, "events-app", "manage"));
	const canPinToCollections = useSelector((state) => canManageByApplication(state, "map-app", "manage"));
	const widgetState = useSelector((state) => (entity ? deviceWidgetConfig(state) : []), isEqual);
	const accessControlWidget = widgetState?.find((widget) => widget.id === "access_control");
	const displayProperties = useSelector((state) => {
		return (entity && feedInfoSelector(feedId)(state)?.displayProperties) || [];
	});
	const dialog = useSelector((state) => state.appState?.dialog?.openDialog || "");

	useEffect(() => {
		if (!forReplay) {
			dispatch(startCamerasInRangeStream(contextId, entityType, "profile"));

			if (getWidgetStatus("activities")) dispatch(startActivityStream(contextId, "device", "profile"));
			if (getWidgetStatus("files")) dispatch(startAttachmentStream(contextId, "profile"));
			if (device?.entityData?.displayTargetId && device?.entityData?.displayType?.toLowerCase() === "facility") {
				facilityService.getFloorPlan(device.entityData.displayTargetId, (err, res) => {
					if (err) {
						setFacilityHidden(true);
					} else if (res.success) {
						setFacilityHidden(false);
					}
				});
			}
		} else {
			// do we need to do something else here, or leave it up to the widgets to pull in the static data
		}

		return () => {
			unSubscribeCameras();
		};
	}, []);

	useEffect(() => {
		if (!forReplay && widgetState?.length > 0) {
			const retryCamera = widgetState.find((element) => element.id === "cameras");
			const { camerasInRange } = context;
			if (retryCamera.enabled && !camerasInRange) {
				dispatch(startCamerasInRangeStream(contextId, entityType, "profile"));
			}
		}
	}, [widgetState, forReplay]);

	useEffect(() => {
		handleScroll();
	}, [scrolledUp]);

	const getWidgetStatus = (widgetId) => {
		const widgetConfig = widgetState;
		const widget = widgetConfig.find((widget, index) => {
			widget.index = index;
			return widget.id === widgetId;
		});

		return widget;
	};

	const handleScroll = () => {
		$(".cb-profile-wrapper").on("resize scroll", () => {
			const elementTop = $(".summary-wrapper").offset().top;
			const elementBottom = elementTop + $(".summary-wrapper").outerHeight() + 120; // offset for app/navigation bar
			const viewportTop = $(".cb-profile-wrapper").scrollTop();
			const profileHeight = $(".cb-profile-wrapper").height();
			const viewportBottom = viewportTop + $(".cb-profile-wrapper").height();
			const widgetsHeight = $(".widgets-container").height();
			const scrollLength = viewportBottom - elementBottom;
			const pctScrolled = Math.floor((viewportTop / scrollLength) * 100); // gets percentage scrolled

			if (!scrolledUp && pctScrolled > 1 && widgetsHeight > profileHeight - 66) {
				setScrolledUp(true);
			} else if (scrolledUp && pctScrolled < 2) {
				setScrolledUp(false);
			}
		});
	};

	const handleExpand = () => {
		setScrolledUp(false);
		$(".cb-profile-wrapper").scrollTop(0);
	};

	const handleEditLayout = (event) => {
		event.preventDefault();
		setLayoutControlsOpen(true);
	};

	const handleCloseEditLayout = () => {
		setLayoutControlsOpen(false);
	};

	const showFloorPlanOnTargetClick = () => {
		const { entityData } = device;
		if (entityData.displayType === "facility" && floorPlansWithFacilityFeed !== null) {
			const floorPlanData = floorPlansWithFacilityFeed[entityData.displayTargetId];
			if (floorPlanData.id === entityData.displayTargetId) {
				selectFloorPlanOn(floorPlanData, floorPlanData.facilityFeedId);
			}
		}
	};

	const getSummaryWidgetActions = () => {
		let actions = [];
		if (
			entityData?.displayType?.toLowerCase() === "facility" &&
			entityData?.displayTargetId &&
			facilityOption &&
			!facilityHidden
		) {
			actions.push({
				name: getTranslation("global.profiles.deviceProfile.facility"),
				nameText: "Facility",
				action: () => window.location.replace(`/facilities-app/#/?${entityData.displayTargetId}`)
			});
		}
		actions = [
			...actions,
			{
				name: getTranslation("global.profiles.deviceProfile.pinTo"),
				nameText: "Pin To",
				action: () => dispatch(openDialog("pinToDialog"))
			}
		];

		// can we edit a device?
		if (canManageDevice && !disableEdit) {
			actions = [
				...actions,
				{
					name: getTranslation("global.profiles.deviceProfile.edit"),
					nameText: "Edit",
					action: () => dispatch(openDialog("deviceEditDialog"))
				}
			];
		}
		return actions;
	};

	const streamVideoWallClusterCameras = cameras => {
		cameras?.forEach(innerArray => {
			innerArray?.forEach(camera => {
				if (camera?.cameraId) {
					cameraIds[contextId] = [...new Set([...(cameraIds[contextId] || []), camera?.cameraId])];
					dispatch(streamIndividualEntityByType(camera.cameraId, "camera", "dock"));
				}
			});
		});
	};

	const unSubscribeCameras = () => {
		if (!contextId || !cameraIds[contextId]) return;

		// Avoid using variables that rely on `useSelector` inside unmount logic, as their values may be stale or inaccessible.
		// Use `window.redux.getState()` to imperatively access the latest Redux state during cleanup.

		const state = window.redux.getState();
		const contextData = state.contextualData;

		// Helper to count how many times a cameraId appears in all devices
		const getCameraIdCount = cameraId =>
			Object.values(cameraIds).reduce((count, ids) => {
				return count + (new Set(ids).has(cameraId) ? 1 : 0);
			}, 0);

		// This function ensures that cameras linked to a specific device context are unsubscribed only if no other devices use them.
		// It checks each camera's usage across all devices and unsubscribes only if the camera is exclusive to the current context.
		// Regardless of usage, it removes the camera ID from the local cameraIds cache for cleanup.

		cameraIds[contextId].forEach(cameraId => {
			const cameraContext = contextData[cameraId];
			if (!cameraContext) return;

			const subscriptions = [].concat(cameraContext?.subscriptions?.entity?.subscription || []);
			if (!subscriptions.length) return;

			const isOnlyUsedHere = getCameraIdCount(cameraId) === 1;

			if (isOnlyUsedHere) {
				subscriptions.forEach(sub => {
					if (sub !== "shared") {
						dispatch(unsubscribeFromContext(cameraId, "dock"));
						dispatch(unsubscribe(sub));
					}
				});
			}

			// Always remove from local cache for this device
			const updatedIds = cameraIds[contextId].filter(id => id !== cameraId);
			updatedIds.length ? (cameraIds[contextId] = updatedIds) : delete cameraIds[contextId];
		});
	};

	const widgetRenderFilters = {
		activities: !(context?.activities || forReplay),
		files: !context?.attachments,
		access_control: !canControlDevice,
		linked_items: disableLinkedItems,
		cameras: !camerasInRange,
		map: !widgetsExpandable
	};

	const renderWidgets = (widgetName) => {
		switch (widgetName) {
			case "details":
				return (
					<DetailsWidget
						id={widgetName}
						key={`${contextId}-details`}
						details={displayProperties.length > 0 ? entityData?.properties : {}}
						displayProps={displayProperties}
						enabled={!widgetRenderFilters.details}
					/>
				);
			case "map":
				return <MapWidget id={widgetName} selectWidget={selectWidget} entityType={entityType} />;
			case "access_control":
				return (
					<AccessControlWidget
						id={widgetName}
						key={`${contextId}-access_control`}
						contextId={contextId}
						enabled={accessControlWidget?.enabled}
					/>
				);

			case "files":
				return (
					<FileWidget
						id={widgetName}
						key={`${contextId}-files`}
						canDelete={!readOnly && canManageDevice}
						hasAccess={canAccess}
						selected={view === widgetName}
						selectWidget={selectWidget}
						contextId={contextId}
						subscriberRef="profile"
						widgetsExpandable={widgetsExpandable}
					/>
				);

			case "activities":
				return (
					<Activities
						id={widgetName}
						key={`${contextId}-activities`}
						canManage={canAccess}
						selected={view === widgetName}
						pageSize={5}
						selectWidget={selectWidget}
						contextId={contextId}
						subscriberRef="profile"
						readOnly={readOnly}
						forReplay={forReplay}
						endDate={endDate}
						displayType={type === "Radar" ? type : "Device"}
						widgetsExpandable={widgetsExpandable}
						profile="device"
					/>
				);

			case "linked_items":
				return (
					<LinkedItemsWidget
						id={widgetName}
						selected={view === widgetName}
						expanded={false} // for styling differences between profile and expanded widget views
						canLink={canManageDevice}
						disabledTypes={disabledLinkedItemTypes}
						selectWidget={selectWidget}
						view={view}
						contextId={contextId}
						subscriberRef={"profile"}
						widgetsExpandable={widgetsExpandable}
					/>
				);

			case "cameras":
				return (
					<CamerasWidget
						id={widgetName}
						key={`${contextId}-cameras`}
						canLink={!readOnly && canManageDevice}
						unlinkCameras={unlinkEntities}
						contextId={contextId}
						unsubscribeFromFeed={unsubscribeFromFeed}
						subscriberRef="profile"
						readOnly={readOnly}
						disableSlew={readOnly}
						selectFloorPlanOn={selectFloorPlanOn}
						floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
					/>
				);
			case "cameraClusters":
				return (
					<CameraClusters
						id={widgetName}
						key={contextId}
						streamVideoWallClusterCameras={streamVideoWallClusterCameras}
						contextId={contextId}
						updateEntity={updateDevice}
					/>
				);
			case "alerting_zones":
				return (
					<AlertingZonesWidget
						id={widgetName}
						key={contextId}
						selected={view === widgetName}
						selectWidget={selectWidget}
						contextId={contextId}
						readOnly={readOnly}
						widgetsExpandable={widgetsExpandable}
						updateEntity={updateDevice}
					/>
				);
			default:
				return null;
		}
	};

	if (context) {
		// Height of summary-info (consistent between scrolled and not scrolled state) + padding
		const scrollOffset = scrolledUp ? 167 : 0;
		// Dynamic offset for widget container when SummaryWidget is collapsed
		const widgetsContainerStyle = {
			top: scrollOffset
		};

		return (
			<div className="cb-profile-wrapper" style={{ height: "100%" }}>
				<div style={{ display: layoutControlsOpen ? "block" : "none" }}>
					<ErrorBoundary>
						<LayoutControls
							close={handleCloseEditLayout}
							widgetOrder={widgetState}
							profile="device"
							layoutControlsOpen={layoutControlsOpen}
						/>
					</ErrorBoundary>
				</div>
				<div style={{ display: layoutControlsOpen ? "none" : "block" }}>
					<ErrorBoundary>
						<SummaryWidget
							id={contextId}
							readOnly={readOnly}
							contextId={contextId}
							name={name}
							type="device"
							geometry={geometry}
							description={description}
							scrolledUp={scrolledUp}
							handleExpand={handleExpand}
							selectFloor={showFloorPlanOnTargetClick}
							actions={getSummaryWidgetActions()}
							displayType={type === "Radar" ? type : "Device"}
						/>
					</ErrorBoundary>
					{!scrolledUp && (
						<div className="layout-control-button">
							<a className="cb-font-link" onClick={handleEditLayout}>
								<Translate value="global.profiles.deviceProfile.editProfileLayout" />
							</a>
						</div>
					)}
					<div className="widgets-container" style={widgetsContainerStyle}>
						{widgetState
							.filter((element) => getWidgetStatus(element.id))
							.map((element) => {
								const { id, enabled } = element;
								if (widgetRenderFilters[id]) {
									return false;
								}
								if (!enabled) {
									return false;
								}
								return <ErrorBoundary key={id}>{renderWidgets(id)}</ErrorBoundary>;
							})}
						{dialog === "deviceEditDialog" && (
							<DeviceEditDialog
								device={device}
								entityType={entityType}
								close={() => dispatch(closeDialog("deviceEditDialog"))}
								dir={dir}
							/>
						)}
						<PinToDialog
							close={() => dispatch(closeDialog("pinToDialog"))}
							entity={device}
							addRemoveFromCollections={addRemoveFromCollections}
							addRemoveFromEvents={addRemoveFromEvents}
							createCollection={createCollection}
							openDialog={openDialog}
							closeDialog={closeDialog}
							canManageEvents={canManageEvents}
							canPinToCollections={canPinToCollections}
							dir={dir}
						/>
					</div>
				</div>
			</div>
		);
	} else {
		return <div />;
	}
};

DeviceProfile.propTypes = propTypes;
DeviceProfile.defaultProps = defaultProps;
export default DeviceProfile;
